/**
 * Performances (fase 2, steg 4).
 *
 * ---------------------------------------------------------------------------------------------
 * THE BOOKING CHAIN IS AN ORDERED STATUS, NOT A STATE MACHINE (decision 0011).
 *
 * Atle, 29 August: "Det trenger ikke være låst i rekkefølge. Vi må kunne f.eks hoppe rett inn i
 * steg 4." So `PERFORMANCE_PROGRESS` below is the order the chain is DRAWN in, and no transition
 * is refused. A small artist who just signs and plays goes from `enquiry_sent` straight to
 * `signed`, and a system that makes somebody click through four intermediate steps to describe
 * something that already happened gets lied to — which makes the status useless to everyone
 * reading it afterwards.
 *
 * The order is exported as DATA rather than left implicit in the enum, for two reasons. `cancelled`
 * is not step nine: it is an ending reachable from anywhere, so it belongs in the enum and not on
 * the progress line. And a client drawing a progress bar needs the order without hardcoding it,
 * which is the same rule the permission matrix follows.
 *
 * WHAT MOVING THE CHAIN COSTS: `performance:update`, like any other field. There is deliberately no
 * `performance:set_status` cell — a second rule saying the same thing is a rule that eventually
 * disagrees with the first. The audit log records every move with who, when, from and to, so the
 * history survives however somebody got there.
 *
 * THE ARTIST IS A CONTACT, AND IT IS NULLABLE. `contacts` is the register the product already
 * points at, so an artist booked two years running is one contact with two performances — the
 * question a booker actually asks. Nullable because a wishlist entry is usually a name somebody
 * heard before it is anybody's contact.
 *
 * `title` IS NOT THE ARTIST'S NAME REPEATED. It is what goes on the poster when that differs from
 * who is contracted: a DJ alias, a one-off collaboration, "Support TBA".
 *
 * TIMES ARE INSTANTS, UNLIKE AN EDITION'S DATES. An edition spans calendar days; a performance
 * starts at a moment. A 23:30 set on the Saturday belongs to Saturday's running order and to
 * Sunday's date, and only an instant carries that without lying. Both nullable: a booking is
 * confirmed long before the running order is drawn.
 * ---------------------------------------------------------------------------------------------
 */
import { z } from 'zod';
import { engagementRefDtoSchema } from './engagements.js';

import { performanceStatusSchema } from '../enums.js';
import { cursorQuerySchema, page } from '../http/pagination.js';
import { rowViewerSchema } from './row-viewer.js';

/**
 * The chain, in the order it is drawn — and WITHOUT `cancelled`.
 *
 * A client renders progress from this. `cancelled` is deliberately absent: it is an ending, not a
 * step, and putting it at the end of a progress line would say a cancelled booking is further
 * along than a signed one.
 */
export const PERFORMANCE_PROGRESS = [
  'wishlist',
  'enquiry_sent',
  'hold',
  'verbally_confirmed',
  'deal_memo',
  'contract_sent',
  'signed',
  'announced',
  'performed',
] as const;

export const PERFORMANCE_SORT_KEYS = ['order', 'time', 'status'] as const;
export type PerformanceSortKey = (typeof PERFORMANCE_SORT_KEYS)[number];

/** The artist, as much of them as a line-up row needs. The full contact is one request away. */
export const performanceArtistSchema = z
  .object({
    id: z.uuid(),
    displayName: z.string(),
    companyName: z.string().nullable(),
  })
  .meta({ id: 'PerformanceArtist' });

/**
 * REGISTERRADEN POSTEN KOM FRA (0141, QA K4).
 *
 * Skilt fra `artist` framfor å slås sammen med den, fordi de to peker på HVER SIN TING: `artist`
 * er en kontakt i festivalens register, denne er raden i bookingregisteret. De to lenker ulike
 * steder, og en felles form ville gjort det umulig for skjermen å vite hvor klikket skal.
 *
 * Én av dem holder: en post flyttet fra Booking har denne, en post laget for hånd har den andre.
 */
export const performanceBookingArtistSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
  })
  .meta({ id: 'PerformanceBookingArtist' });

/** The stage, likewise: enough to draw a row, not the whole record. */
export const performanceStageSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
  })
  .meta({ id: 'PerformanceStage' });

export const performanceDtoSchema = z
  .object({
    id: z.uuid(),
    editionId: z.uuid(),
    /**
     * The festival the year belongs to, carried so a list that SPANS years can be read.
     *
     * A line-up never needed it: every row is one edition of one festival, and the page already
     * knows both from its own address. The contact's own page is the first list that does not —
     * "hvor har vi hatt denne artisten" crosses festivals — and a row it cannot link to is a row
     * nobody can open. It is one column off a join the row already needs to exist.
     */
    festivalId: z.uuid(),
    /** The name of the year it played, for the same reason: a cross-year list has to say which. */
    editionName: z.string(),
    /** What goes on the poster, when that differs from the contact's name. */
    title: z.string().nullable(),
    /** Null for a guest: the booking chain is contract status, and a contract is never shared (239.31). */
    status: performanceStatusSchema.nullable(),
    /** Resolved in the same statement as the row — never a second request per line. */
    artist: performanceArtistSchema.nullable(),
    /** Registerraden, når posten ble flyttet fra Booking. Se skjemaet for hvorfor den er egen. */
    bookingArtist: performanceBookingArtistSchema.nullable(),
    /**
     * OM DENNE RADEN NÅS GJENNOM EN DELT ADVANCE FRAMFOR GJENNOM ENS EGET HUS (QA H4).
     *
     * Skjermen trenger å vite det uavhengig av HVILKEN adresse man kom fra. Byrået har sin egen
     * rute, men ingenting hindrer dem i å skrive inn festivalens — og da fikk de festivalens
     * grensesnitt med «Rediger kontrakt» og «Ny linje» i Økonomi. Dataene lakk ikke (RLS holdt),
     * men skjermen tilbød handlinger som alltid ville blitt avvist.
     *
     * Et FELT og ikke en rute-prop: sannheten om hvem som eier raden bor i raden.
     */
    shared: z.boolean(),
    /**
     * OM DET FINNES EN DELT ADVANCE PÅ DENNE RADEN (sak 113.2).
     *
     * Ikke det samme som `shared`, som sier «jeg er motparten». Denne er sann for BEGGE hus —
     * og den er der fordi eieren ellers ikke har noen måte å vite at noen andre er inne.
     *
     * Atle valgte 113.2: festivalen slipper overføringsplukkeren, og får vite hvorfor. Uten
     * dette feltet ville setningen ikke visst når den skulle stå.
     */
    advanceShared: z.boolean(),
    /**
     * 310.12: RADEN ER EN KONSERTS ELLER ET ARRANGEMENTS INNSLAG, ikke en programpost på en festival.
     * Siden heter da «Konsert» — artisten som åpner konserten sin, så «Programpost».
     */
    onConcert: z.boolean().default(false),
    /**
     * HVILKE FANER LESEREN ER DELT (QA5 C-H2). `null` er «ingen innsnevring»: huset selv, og en
     * ekstern delt hele programposten. Ellers delingens omfang — `shareCovers` avgjør fanene, og
     * API-et avviser de samme (`@ShareScope`). Standard null, så en klient mot et eldre API
     * leser svaret framfor å avvise det.
     */
    viewerScopes: z.array(z.string()).nullable().default(null),
    /**
     * EN ANNEN KONSERT PÅ SAMME SCENE TIL SAMME TID (QA-punkt 17).
     *
     * Regnet på serveren og ikke i klienten: lineupen er paget, og to rader som kolliderer kan
     * ligge på hver sin side av et sideskift. En klient som talte det den hadde hentet ville
     * meldt fra om noen kollisjoner og tiet om resten — verre enn å ikke melde fra i det hele
     * tatt, fordi man da tror man har fått beskjed.
     */
    clashes: z.boolean(),
    stage: performanceStageSchema.nullable(),
    startsAt: z.iso.datetime().nullable(),
    endsAt: z.iso.datetime().nullable(),
    notes: z.string().nullable(),
    sortOrder: z.number().int(),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
    /** Engasjementet raden hører til (0927, grep F1): den ene statusen alle flatene leser. */
    engagement: engagementRefDtoSchema.nullable().optional(),
  })
  .meta({ id: 'Performance' });

export const listPerformancesQuerySchema = cursorQuerySchema(PERFORMANCE_SORT_KEYS)
  .extend({
    /** The line-up of one year. The page always sends it; the API does not require it. */
    editionId: z.uuid().optional(),
    stageId: z.uuid().optional(),
    /**
     * Every booking of one artist, across every year and every festival (sak 49).
     *
     * The contact's own page asks it — "hvor har vi hatt denne artisten" is the question a booker
     * asks before making an offer, and it is the one thing the register cannot answer from the
     * contact row. It is deliberately NOT narrowed to an edition: the answer is more useful the
     * further back it goes, and `editionId` is right there for a caller who wants one year.
     *
     * A FILTER RATHER THAN A ROUTE ON THE CONTACT. `GET /v1/contacts/:id/performances` would be a
     * second way to read the line-up, with its own scope handling and its own cursor, and the two
     * would answer differently the first time one of them learned something. The line-up endpoint
     * already knows how to page performances and who may see them.
     */
    artistContactId: z.uuid().optional(),
    /**
     * FRITEKSTSØK (migrering 0107). Atle, 9. september: «ALLE TABELLER MÅ KUNNE SØKES OG
     * SORTERES!!!»
     *
     * I BASEN OG IKKE I KLIENTEN, fordi lista er paget: et filter i nettleseren ville søkt i den
     * siden som tilfeldigvis var hentet, og svart «ingen treff» når det står tolv på side tre. Det
     * er en feil ingen kan se. Går gjennom `app.search_performance_ids`, som kontaktsøket og
     * artistsøket gjør, mot en trigram-indeks.
     *
     * Søker i bookingens egen tittel og notatet. Artistnavnet bor i `contacts` og har
     * sitt eget filter — `artistContactId` — framfor en join en indeks ikke kan bære.
     */
    search: z.string().trim().min(1).max(120).optional(),
    /**
     * OR within the filter, like the task list's: a performance matches if its status is any of
     * the ones asked for. "What is still unsigned" is several statuses, not one.
     */
    status: z
      .string()
      .transform((raw) => raw.split(',').map((part) => part.trim()))
      .pipe(z.array(performanceStatusSchema).min(1).max(9))
      .optional(),
  })
  .meta({ id: 'ListPerformancesQuery' });

export const performanceListResponseSchema = page(performanceDtoSchema, 'PerformanceListResponse');

export const createPerformanceRequestSchema = z
  .object({
    editionId: z.uuid(),
    title: z.string().trim().min(1).max(200).nullish(),
    artistContactId: z.uuid().nullish(),
    /**
     * REGISTERRADEN, NÅR POSTEN KOMMER FRA BOOKING (0141, QA K4).
     *
     * Settes av overføringen og ikke av skjemaet: en post man lager for hånd har ingen registerrad
     * bak seg, og å tilby feltet i skjemaet ville vært å be brukeren koble to ting systemet vet.
     */
    bookingArtistId: z.uuid().nullish(),
    stageId: z.uuid().nullish(),
    status: performanceStatusSchema.optional(),
    startsAt: z.iso.datetime().nullish(),
    endsAt: z.iso.datetime().nullish(),
    notes: z.string().trim().max(4000).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => (body.title ?? '').length > 0 || body.artistContactId != null, {
    message: 'A performance needs either a title or an artist.',
    path: ['title'],
  })
  .refine((body) => body.startsAt == null || body.endsAt == null || body.endsAt > body.startsAt, {
    message: 'The end must be after the start.',
    path: ['endsAt'],
  })
  .meta({ id: 'CreatePerformanceRequest' });

/**
 * Everything except which edition it belongs to.
 *
 * NO `editionId`. A performance that changed year would carry its rider, its logistics and its
 * guest list into another year's history with nothing able to show it had happened — the same
 * reason `updateEditionRequestSchema` has no `festivalId`. Booking the same artist next year is a
 * new performance, which is also what it is.
 */
export const updatePerformanceRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(200).nullish(),
    artistContactId: z.uuid().nullish(),
    stageId: z.uuid().nullish(),
    status: performanceStatusSchema.optional(),
    startsAt: z.iso.datetime().nullish(),
    endsAt: z.iso.datetime().nullish(),
    notes: z.string().trim().max(4000).nullish(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .refine((body) => body.startsAt == null || body.endsAt == null || body.endsAt > body.startsAt, {
    message: 'The end must be after the start.',
    path: ['endsAt'],
  })
  .meta({ id: 'UpdatePerformanceRequest' });

export type PerformanceDto = z.infer<typeof performanceDtoSchema>;
export type ListPerformancesQuery = z.infer<typeof listPerformancesQuerySchema>;
export type PerformanceListResponse = z.infer<typeof performanceListResponseSchema>;
export type CreatePerformanceRequest = z.infer<typeof createPerformanceRequestSchema>;
export type UpdatePerformanceRequest = z.infer<typeof updatePerformanceRequestSchema>;

/**
 * Reordering the line-up (Atle, 29 August, answer D: "drag n drop må fungere ja").
 *
 * ---------------------------------------------------------------------------------------------
 * THE WHOLE ORDER, IN ONE REQUEST, RATHER THAN ONE MOVED ROW.
 *
 * A drag produces "this act now sits third", and there are two ways to say that. Sending only the
 * moved row means computing a `sortOrder` BETWEEN its new neighbours, which needs gaps in the
 * numbering, and gaps run out: drop enough acts into the same slot and there is no integer left
 * between 40 and 41. The fix for that is renumbering, which is this request anyway — so it may as
 * well be the only path and not the fallback nobody exercises.
 *
 * So the client sends the ids in the order they now appear, and the server writes `sort_order` as
 * the position in that array. It is idempotent, it needs no gaps, and the numbers stay small enough
 * to read in a database session.
 *
 * A LINE-UP IS TENS OF ROWS, NOT THOUSANDS, and that is what makes this affordable. `MAX` is the
 * ceiling that keeps it true: past it the request is refused rather than quietly becoming a write
 * proportional to something unbounded. A festival that outgrows it has outgrown dragging, too.
 *
 * EVERY ID MUST BELONG TO THE EDITION, AND THE SET MUST BE COMPLETE. A partial list would leave the
 * rows it omitted holding positions the listed ones now claim, so the order after the write would
 * not be the order the person saw — and a list containing a performance from another year is how a
 * drag in one tab rewrites another year in another tab. The service checks both against the
 * edition before it writes anything.
 * ---------------------------------------------------------------------------------------------
 */
export const MAX_REORDERED_PERFORMANCES = 500;

export const reorderPerformancesRequestSchema = z
  .object({
    editionId: z.uuid(),
    /** Every performance of the edition, in the order they now appear. Position becomes sortOrder. */
    performanceIds: z.array(z.uuid()).min(1).max(MAX_REORDERED_PERFORMANCES),
  })
  .refine((body) => new Set(body.performanceIds).size === body.performanceIds.length, {
    message: 'The same performance cannot appear twice.',
    path: ['performanceIds'],
  })
  .meta({ id: 'ReorderPerformancesRequest' });

/**
 * What the reorder answers with: how many rows moved, not the page.
 *
 * The caller is holding the list already — they just dragged it — so returning it would be a second
 * copy of what is on screen. The count is the `UPDATE`'s own row count, and it is deliberately not
 * the length of the request: a drag that changed nothing writes nothing and says so.
 */
export const reorderPerformancesResponseSchema = z
  .object({ movedCount: z.number().int().min(0) })
  .meta({ id: 'ReorderPerformancesResponse' });

export type ReorderPerformancesRequest = z.infer<typeof reorderPerformancesRequestSchema>;
export type ReorderPerformancesResponse = z.infer<typeof reorderPerformancesResponseSchema>;
